import { BLANK_QUOTE, defaultFollowUp } from "./utils.js";
import { quoteLineGroups } from "./builderHelpers.js";
import { leanAttachment } from "./draftRecord.js";
import { normalizeBilling } from "./proBono.js";
import { hasCustomWording, normalizePrintWording } from "./printWording.js";

/* ═══ QUOTE SIGNATURES (docs/quotes/quote-saving.md) ═══
   One canonical string for "what this quote says", so "has it changed?" has one
   answer everywhere: a save finishing, a builder replacement, a reload.

   Left out: anything a save stamps or the builder derives (ids, dates of writing,
   totals, step flags, `_` keys, line `key`s), and the builder-only parts of an
   attachment (base64 previews, File objects). Absent, undefined and null read the
   same. */
const VOLATILE_KEYS = new Set([
  "id", "step", "savedToDb", "savedAt", "updatedAt", "createdAt", "draft",
  "subtotal", "total", "totalSavings", "mathVerified", "aiNotes", "aiQuestions",
  // Derived from lineGroups on every write.
  "lineGroupNotes",
]);

// Absent, undefined, null and the empty string read the same: a save writes "" for a
// text the builder never had, and the two must not count as a change.
const canon = (value) => {
  if (Array.isArray(value)) return value.map(canon);
  if (value && typeof value === "object") {
    const out = {};
    for (const k of Object.keys(value).sort()) {
      const v = value[k];
      if (v === undefined || v === null || v === "") continue;
      out[k] = canon(v);
    }
    return out;
  }
  return value;
};

const canonLine = (li) => {
  const { key: _key, isCustomRate: _custom, savedToDb: _saved, ...rest } = li || {};
  return {
    ...rest,
    catalogId: rest.catalogId || null,
    description: rest.description || "",
    notes: rest.notes || "",
    rate: Number(rest.rate) || 0,
    group: rest.group ? String(rest.group).trim() : undefined,
  };
};

const canonDiscount = (d) => {
  const { key: _key, ...rest } = d || {};
  return { ...rest, value: Number(rest.value) || 0 };
};

function canonicalQuote(q) {
  const merged = { ...BLANK_QUOTE(), ...q };
  const out = {};
  for (const k of Object.keys(merged)) {
    if (VOLATILE_KEYS.has(k) || k.startsWith("_")) continue;
    out[k] = merged[k];
  }
  out.lineItems = (merged.lineItems || []).map(canonLine);
  out.quoteDiscounts = (merged.quoteDiscounts || []).map(canonDiscount);
  out.attachments = (merged.attachments || []).map(leanAttachment);
  out.lineGroups = quoteLineGroups(q || {});
  // The defaults a save writes for what the builder left unset (quoteRecord.js), so a
  // quote before its first save and its stored record say the same thing.
  out.docType = merged.docType || "quote";
  out.industry = merged.industry || "other";
  out.leadSource = merged.leadSource || "referral";
  out.jobStatus = merged.jobStatus || "quoted";
  out.billing = normalizeBilling(merged.billing);
  // A wording map of all nulls says nothing a quote with no map doesn't.
  out.printWording = hasCustomWording(merged) ? normalizePrintWording(merged.printWording) : undefined;
  return canon(out);
}

/** The content a save would store. Two quotes with the same string are the same quote. */
export function quoteContentSig(q) {
  return JSON.stringify(canonicalQuote(q));
}

/** The signature of a fresh blank quote dated as this one is, so a blank opened yesterday
 *  isn't "edited" just because today's blank has a different date. A blank's follow-up
 *  always comes from its quote date, so a date someone changed still counts as content. */
export function blankContentSig(q) {
  return quoteContentSig({ ...BLANK_QUOTE(), quoteDate: q?.quoteDate, followUpDate: q?.quoteDate ? defaultFollowUp(q.quoteDate) : q?.followUpDate });
}

/** Does this quote hold anything a fresh blank quote doesn't? */
export const differsFromBlank = (q) => quoteContentSig(q) !== blankContentSig(q);
